/**
 * Pure math helper — extracted from `lib/operatingHealth` so unit tests
 * can require it without dragging in the ComplianceScoreCard chain
 * (which pulls axios via apiClient — Jest can't parse ESM axios).
 *
 * Consumers should keep importing from `lib/operatingHealth` — the
 * helper is re-exported there for API stability.
 */

/**
 * Week-over-week delta chip helper — first history entry ≥6 days older
 * than the last entry vs the last entry's pct. Falls back to the oldest
 * entry when nothing is ≥6 days old so brand-new operators still see a
 * chip after their second snapshot.
 *
 * Returns `{ delta, tone: "up"|"down"|"flat", spanDays }` or null when
 * history has fewer than 2 usable entries.
 */
export function computeWeeklyDelta(history) {
  if (!Array.isArray(history) || history.length < 2) return null;
  const sorted = [...history].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const last = sorted[sorted.length - 1];
  const lastDate = Date.parse(last.date);
  if (Number.isNaN(lastDate)) return null;
  const cutoff = lastDate - 6 * 86400 * 1000;
  const priors = sorted
    .slice(0, -1)
    .filter((h) => {
      const t = Date.parse(h.date);
      return !Number.isNaN(t) && t <= cutoff;
    });
  const prior = priors.length ? priors[priors.length - 1] : sorted[0];
  if (!prior || typeof prior.pct !== "number" || typeof last.pct !== "number") return null;
  const delta = Math.round(last.pct - prior.pct);
  const spanDays = Math.max(
    1,
    Math.round((Date.parse(last.date) - Date.parse(prior.date)) / 86400 / 1000),
  );
  const tone = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  return { delta, tone, spanDays };
}
